import { CONVERSATION_PRIORITY_ORDER } from 'shared/constants/messages';

export const findPendingMessageIndex = (chat, message) => {
  const { echo_id: tempMessageId } = message;
  return chat.messages.findIndex(
    m => m.id === message.id || m.id === tempMessageId
  );
};

// 'all' deja pasar todos los estados, resueltas incluidas, igual que el server
// (ConversationFinder#apply_status_filter).
export const filterByStatus = (chatStatus, filterStatus) =>
  filterStatus === 'all' ? true : chatStatus === filterStatus;

export const filterByInbox = (shouldFilter, inboxId, chatInboxId) => {
  const isOnInbox = Number(inboxId) === chatInboxId;
  return inboxId ? isOnInbox && shouldFilter : shouldFilter;
};

export const filterByTeam = (shouldFilter, teamId, chatTeamId) => {
  const isOnTeam = Number(teamId) === chatTeamId;
  return teamId ? isOnTeam && shouldFilter : shouldFilter;
};

export const filterByLabel = (shouldFilter, labels, chatLabels) => {
  const isOnLabel = labels.every(label => chatLabels.includes(label));
  return labels.length ? isOnLabel && shouldFilter : shouldFilter;
};

export const filterByAssignee = (shouldFilter, assigneeId, chatAssigneeId) => {
  if (!assigneeId) return shouldFilter;
  const isMatchingAssignee = Number(assigneeId) === chatAssigneeId;
  return isMatchingAssignee && shouldFilter;
};

export const filterByUnattended = (
  shouldFilter,
  conversationType,
  unreadCount
) => {
  if (conversationType !== 'unattended') {
    return shouldFilter;
  }
  // For unattended view, only show conversations with unread messages
  return unreadCount > 0 && shouldFilter;
};

// "Desatendida": mismo criterio que Conversation::AWAITING_REPLY_SQL, que sale
// de waiting_since. El payload (API y websocket) la manda como epoch, 0 si no
// hay. La condición de "el cliente escribió" la resuelve el server.
export const isAwaitingReply = conversation =>
  Number(conversation.waiting_since) > 0;

export const filterByAwaitingReply = (
  shouldFilter,
  conversationType,
  conversation
) => {
  if (conversationType !== 'awaiting_reply') return shouldFilter;
  return isAwaitingReply(conversation) && shouldFilter;
};

export const applyPageFilters = (conversation, filters) => {
  const {
    inboxId,
    status,
    labels = [],
    teamId,
    conversationType,
    assigneeId,
  } = filters;
  const {
    status: chatStatus,
    inbox_id: chatInboxId,
    labels: chatLabels = [],
    meta = {},
    first_reply_created_at: firstReplyOn,
    waiting_since: waitingSince,
  } = conversation;
  const team = meta.team || {};
  const { id: chatTeamId } = team;
  const assignee = meta.assignee || {};
  const { id: chatAssigneeId } = assignee;

  let shouldFilter = filterByStatus(chatStatus, status);
  shouldFilter = filterByInbox(shouldFilter, inboxId, chatInboxId);
  shouldFilter = filterByTeam(shouldFilter, teamId, chatTeamId);
  shouldFilter = filterByLabel(shouldFilter, labels, chatLabels);
  shouldFilter = filterByAssignee(shouldFilter, assigneeId, chatAssigneeId);
  shouldFilter = filterByUnattended(
    shouldFilter,
    conversationType,
    conversation.unread_count
  );
  shouldFilter = filterByAwaitingReply(
    shouldFilter,
    conversationType,
    conversation
  );

  return shouldFilter;
};

const SORT_OPTIONS = {
  last_activity_at_asc: ['sortOnLastActivityAt', 'asc'],
  last_activity_at_desc: ['sortOnLastActivityAt', 'desc'],
  created_at_asc: ['sortOnCreatedAt', 'asc'],
  created_at_desc: ['sortOnCreatedAt', 'desc'],
  priority_asc: ['sortOnPriority', 'asc'],
  priority_desc: ['sortOnPriority', 'desc'],
  waiting_since_asc: ['sortOnWaitingSince', 'asc'],
  waiting_since_desc: ['sortOnWaitingSince', 'desc'],
  lead_score_desc: ['sortOnLeadScore', 'desc'],
};
const sortAscending = (valueA, valueB) => valueA - valueB;
const sortDescending = (valueA, valueB) => valueB - valueA;

const getSortOrderFunction = sortOrder =>
  sortOrder === 'asc' ? sortAscending : sortDescending;

const sortConfig = {
  sortOnLastActivityAt: (a, b, sortDirection) =>
    getSortOrderFunction(sortDirection)(a.last_activity_at, b.last_activity_at),

  sortOnCreatedAt: (a, b, sortDirection) =>
    getSortOrderFunction(sortDirection)(a.created_at, b.created_at),

  sortOnPriority: (a, b, sortDirection) => {
    const DEFAULT_FOR_NULL = sortDirection === 'asc' ? 5 : 0;

    const p1 = CONVERSATION_PRIORITY_ORDER[a.priority] || DEFAULT_FOR_NULL;
    const p2 = CONVERSATION_PRIORITY_ORDER[b.priority] || DEFAULT_FOR_NULL;

    return getSortOrderFunction(sortDirection)(p1, p2);
  },

  // Mismo orden que `sort_on_lead_score` en el backend: las no evaluadas al
  // final y, entre iguales, la de actividad más reciente primero.
  sortOnLeadScore: (a, b, sortDirection) => {
    const hasA = Number.isInteger(a.lead_score);
    const hasB = Number.isInteger(b.lead_score);
    if (hasA !== hasB) return hasA ? -1 : 1;
    if (hasA && a.lead_score !== b.lead_score) {
      return getSortOrderFunction(sortDirection)(a.lead_score, b.lead_score);
    }
    return sortDescending(a.last_activity_at, b.last_activity_at);
  },

  sortOnWaitingSince: (a, b, sortDirection) => {
    const sortFunc = getSortOrderFunction(sortDirection);
    if (!a.waiting_since || !b.waiting_since) {
      if (!a.waiting_since && !b.waiting_since) {
        return sortFunc(a.created_at, b.created_at);
      }
      return sortFunc(a.waiting_since ? 0 : 1, b.waiting_since ? 0 : 1);
    }

    return sortFunc(a.waiting_since, b.waiting_since);
  },
};

export const sortComparator = (a, b, sortKey) => {
  const [sortMethod, sortDirection] =
    SORT_OPTIONS[sortKey] || SORT_OPTIONS.last_activity_at_desc;
  return sortConfig[sortMethod](a, b, sortDirection);
};
